import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type RefObject,
} from "react";
import {
  getCachedBannerHeight,
  removeCachedBannerHeight,
  setCachedBannerHeight,
} from "../helpers/brazeBannerHeightCache";
import { getBannerLayoutEntry } from "../helpers/brazeBannerLayoutRegistry";
import {
  applySlotElementHeight,
  clearSlotElementHeight,
  getBannerContentEl,
  isSlotLayoutVisible,
  measureBannerSlotHeight,
} from "../helpers/brazeBannerLayoutUtils";

export type UseBrazeBannerSlotLayoutOptions = {
  placementId: string;
  containerRef: RefObject<HTMLDivElement | null>;
  showBrazeBanner: boolean;
  /** When false, only the slot element height is synced (no :root CSS vars). */
  syncGlobalLayout?: boolean;
  /** Smooth height transition on inline slots after first measure (not global layout). */
  animateHeight?: boolean;
  /** When false, clear global layout vars but keep measured state for instant restore. */
  placementActive?: boolean;
  /** Keep slot DOM mounted while Braze loads; avoids remount flicker on global banners. */
  keepMounted?: boolean;
};

/**
 * Braze banner layout: apply session-cached height only after Braze confirms a banner
 * (`showBrazeBanner`), then measure from creative and persist. Never reserves global
 * layout while waiting or when no campaign is returned.
 */
export function useBrazeBannerSlotLayout({
  placementId,
  containerRef,
  showBrazeBanner,
  syncGlobalLayout = true,
  animateHeight = false,
  placementActive = true,
  keepMounted = false,
}: UseBrazeBannerSlotLayoutOptions): void {
  const entry = getBannerLayoutEntry(placementId);
  const lastAppliedRef = useRef(0);
  const isShowingRef = useRef(false);
  const hasCachedReserveRef = useRef(false);

  const applyGlobalLayout = useCallback(
    (px: number) => {
      if (!syncGlobalLayout || !entry) return;
      if (!placementActive) {
        entry.clearLayout?.();
        return;
      }
      if (px > 0) {
        entry.applyLayout?.(px);
      } else {
        entry.clearLayout?.();
      }
    },
    [entry, syncGlobalLayout, placementActive]
  );

  const applyHeight = useCallback(
    (px: number, fromCache = false) => {
      const rounded = Math.round(px);
      if (rounded <= 0) return;
      if (rounded === lastAppliedRef.current) return;

      const container = containerRef.current;
      const shouldAnimate =
        animateHeight && !fromCache && lastAppliedRef.current === 0;

      if (shouldAnimate && container) {
        container.style.height = "0px";
        requestAnimationFrame(() => {
          applySlotElementHeight(container, rounded, { animate: true });
        });
      } else {
        applySlotElementHeight(container, rounded, {
          animate: animateHeight && !fromCache,
        });
      }

      lastAppliedRef.current = rounded;
      applyGlobalLayout(rounded);
      setCachedBannerHeight(placementId, rounded);
    },
    [animateHeight, applyGlobalLayout, containerRef, placementId]
  );

  const softClearLayout = useCallback(() => {
    lastAppliedRef.current = 0;
    hasCachedReserveRef.current = false;
    clearSlotElementHeight(containerRef.current);
    applyGlobalLayout(0);
  }, [applyGlobalLayout, containerRef]);

  const clearLayout = useCallback(() => {
    softClearLayout();
    removeCachedBannerHeight(placementId);
  }, [softClearLayout, placementId]);

  useLayoutEffect(() => {
    isShowingRef.current = showBrazeBanner;

    if (!placementActive) {
      if (!keepMounted) {
        applyGlobalLayout(0);
      }
      return;
    }

    if (!showBrazeBanner) {
      if (keepMounted) {
        softClearLayout();
      } else {
        clearLayout();
      }
      return;
    }

    const cached = getCachedBannerHeight(placementId);
    if (cached != null && cached > 0) {
      hasCachedReserveRef.current = true;
      applyHeight(cached, true);
      return;
    }

    if (lastAppliedRef.current > 0) {
      applyGlobalLayout(lastAppliedRef.current);
      applySlotElementHeight(containerRef.current, lastAppliedRef.current);
    }
  }, [
    showBrazeBanner,
    placementActive,
    keepMounted,
    placementId,
    applyHeight,
    clearLayout,
    softClearLayout,
    applyGlobalLayout,
    containerRef,
  ]);

  useEffect(() => {
    if (!showBrazeBanner || !placementActive || !containerRef.current) return;

    const container = containerRef.current;
    const observed = new Set<Element>();
    let measureRaf = 0;

    const syncMeasured = () => {
      if (!isShowingRef.current || !isSlotLayoutVisible(container)) return;
      const measured = measureBannerSlotHeight(container);
      if (measured > 0) {
        applyHeight(measured, hasCachedReserveRef.current);
        hasCachedReserveRef.current = false;
      }
    };

    const scheduleSyncMeasured = () => {
      cancelAnimationFrame(measureRaf);
      measureRaf = requestAnimationFrame(syncMeasured);
    };

    const resizeObserver = new ResizeObserver(scheduleSyncMeasured);

    const observeContent = (el: Element) => {
      if (observed.has(el)) return;
      observed.add(el);
      resizeObserver.observe(el);
    };

    observeContent(container);

    const mutationObserver = new MutationObserver(() => {
      const content = getBannerContentEl(container);
      if (content) observeContent(content);
      scheduleSyncMeasured();
    });
    mutationObserver.observe(container, { childList: true, subtree: true });

    const content = getBannerContentEl(container);
    if (content) observeContent(content);
    scheduleSyncMeasured();

    return () => {
      cancelAnimationFrame(measureRaf);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [showBrazeBanner, placementActive, containerRef, applyHeight]);

  useLayoutEffect(() => {
    return () => {
      clearLayout();
    };
  }, [clearLayout]);
}
